import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  TextField,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Security,
  WarningAmber,
  ContentCopy,
  Autorenew,
} from "@mui/icons-material";
import { useState } from "react";
import axios from "axios";

import { APP_API_BASE } from "../../../config/api";
const API_BASE =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  `${APP_API_BASE}`;

export default function SecurityActionsCard({
  deviceId,
  latestCredentials,
  onRegenerated,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const isTls =
    String(latestCredentials?.security_mode || "PLAINTEXT").toUpperCase() ===
    "TLS";

  const copy = (v) => navigator.clipboard.writeText(v);

  const regenerate = async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Auth token missing");

      const res = await axios.post(
        `${API_BASE}/api/v1/devices/${deviceId}/regenerate-credentials`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      sessionStorage.removeItem(`device_password_viewed_${deviceId}`);
      onRegenerated(res.data);
    } catch (err) {
      console.error("Failed to regenerate credentials", err);
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => setOpen(false);

  return (
    <>
      <Card
        sx={{
          borderRadius: "16px",
          border: "1px solid #E2E8F0",
          boxShadow: "0 4px 18px rgba(0,0,0,0.05)",
        }}
      >
        <CardContent>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: "10px",
                background: "linear-gradient(135deg,#F58A20,#FB923C)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(245,138,32,0.3)",
              }}
            >
              <Security sx={{ color: "#fff", fontSize: 20 }} />
            </Box>

            <Typography
              sx={{ fontWeight: 700, fontSize: 15, color: "#0E2748" }}
            >
              Security Actions
            </Typography>
          </Box>

          <Alert
            icon={<WarningAmber />}
            severity="warning"
            sx={{ mb: 2, borderRadius: "10px", fontSize: 13 }}
          >
            {isTls
              ? "Regenerating credentials rotates the platform-side device secret. TLS certificates stay managed by the broker workflow."
              : "Regenerating credentials will rotate the device secret and may require the device configuration to be updated immediately."}
          </Alert>

          <Button
            startIcon={<Autorenew />}
            variant="contained"
            onClick={() => setOpen(true)}
            sx={{
              background: "#F58A20",
              fontWeight: 600,
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              "&:hover": {
                background: "#e67812",
              },
            }}
          >
            Regenerate Credentials
          </Button>
        </CardContent>
      </Card>

      <Dialog open={open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: "#0E2748" }}>
          Regenerate Device Credentials
        </DialogTitle>

        <DialogContent>
          {!latestCredentials ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              This action rotates the platform-side device secret. Plain MQTT
              devices must use the new password immediately, while TLS
              certificate files remain unchanged unless reissued by the
              broker-side cert sync.
            </Alert>
          ) : isTls ? (
            <Alert severity="success" sx={{ mb: 2 }}>
              Platform credentials were refreshed. TLS certificate filenames
              remain unchanged unless the broker-side cert sync reissues them.
            </Alert>
          ) : (
            <Alert severity="success" sx={{ mb: 2 }}>
              Credentials regenerated successfully. Copy the password now — it
              will not be shown again.
            </Alert>
          )}

          {latestCredentials?.password && !isTls && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
              <TextField
                label="New Device Password"
                value={latestCredentials.password}
                fullWidth
                InputProps={{
                  readOnly: true,
                  sx: {
                    fontFamily: "monospace",
                  },
                }}
              />

              <Tooltip title="Copy password">
                <IconButton
                  onClick={() => copy(latestCredentials.password)}
                  sx={{
                    background: "#EFF6FF",
                    border: "1px solid #BFDBFE",
                    "&:hover": {
                      background: "#DBEAFE",
                    },
                  }}
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          {!latestCredentials ? (
            <>
              <Button onClick={closeDialog}>Cancel</Button>

              <Button
                variant="contained"
                onClick={regenerate}
                disabled={loading}
                sx={{
                  background: "#F58A20",
                  fontWeight: 600,
                  textTransform: "none",
                  "&:hover": {
                    background: "#e67812",
                  },
                }}
              >
                Regenerate
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={closeDialog}
              sx={{
                background: "#0E2748",
                textTransform: "none",
                fontWeight: 600,
              }}
            >
              Done
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
}
